import { useState, useImperativeHandle, forwardRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { projectMemberService, type AddProjectMemberData } from '@/services/projectMemberService'
import { userService, type User } from '@/services/userService'

interface AddProjectMemberModalProps {
  canDirectAdd: boolean // platform owner or project owner
  canRequestAdd: boolean // platform owner or project owner or project admin
  onSuccess: () => void
}

export interface AddProjectMemberModalRef {
  open: () => void
  close: () => void
}

const AddProjectMemberModal = forwardRef<AddProjectMemberModalRef, AddProjectMemberModalProps>(
  ({ canDirectAdd, canRequestAdd, onSuccess }, ref) => {
    const { t } = useTranslation('addProjectMemberModal')
    const { projectId } = useParams<{ projectId: string }>()
    const [isOpen, setIsOpen] = useState(false)
    const [processing, setProcessing] = useState(false)
    const [success, setSuccess] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')

    // Form state
    const [selectedUser, setSelectedUser] = useState<User | null>(null)
    const [role, setRole] = useState<'owner' | 'admin' | 'member' | 'viewer' | 'observer'>('member')
    const [actionType, setActionType] = useState<'direct' | 'request'>('direct')
    const [errors, setErrors] = useState<{ [key: string]: string }>({})

    // User selector state
    const [showUserSelector, setShowUserSelector] = useState(false)
    const [users, setUsers] = useState<User[]>([])
    const [userSearchQuery, setUserSearchQuery] = useState('')
    const [userPage, setUserPage] = useState(1)
    const [userTotalItems, setUserTotalItems] = useState(0)
    const [loadingUsers, setLoadingUsers] = useState(false)
    const userPageSize = 5

    useImperativeHandle(ref, () => ({
      open: () => {
        setIsOpen(true)
        setSuccess(false)
        setSuccessMessage('')
        setSelectedUser(null)
        setRole('member')
        setActionType(canDirectAdd ? 'direct' : 'request')
        setErrors({})
        setShowUserSelector(false)
        setUserSearchQuery('')
        setUserPage(1)
      },
      close: () => {
        setIsOpen(false)
        setSuccess(false)
        setSuccessMessage('')
        setSelectedUser(null)
        setRole('member')
        setErrors({})
        setShowUserSelector(false)
      },
    }))

    // Fetch users when selector is opened
    useEffect(() => {
      if (showUserSelector) {
        fetchUsers()
      }
    }, [showUserSelector, userPage, userSearchQuery])

    const fetchUsers = async () => {
      setLoadingUsers(true)
      try {
        const response = await userService.getUsers({
          limit: userPageSize,
          offset: (userPage - 1) * userPageSize,
          q: userSearchQuery || undefined
        })
        setUsers(response.users || [])
        setUserTotalItems(response.total || 0)
      } catch (err) {
        console.error('Failed to fetch users:', err)
      } finally {
        setLoadingUsers(false)
      }
    }

    const validateForm = () => {
      const newErrors: { [key: string]: string } = {}

      if (!selectedUser) {
        newErrors.user = t('errors.userRequired', 'User is required')
      }

      if (!role) {
        newErrors.role = t('errors.roleRequired', 'Role is required')
      }

      setErrors(newErrors)
      return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async () => {
      if (!validateForm() || !projectId || !selectedUser) return

      setProcessing(true)

      try {
        const data: AddProjectMemberData = {
          user_id: selectedUser.id,
          role
        }

        if (actionType === 'direct') {
          await projectMemberService.addMember(projectId, data)
          setSuccessMessage(t('successDirect', 'Member added successfully!'))
        } else {
          await projectMemberService.requestAddMember(projectId, data)
          setSuccessMessage(t('successRequest', 'Member addition request submitted successfully!'))
        }

        setSuccess(true)
        onSuccess()

        // Close modal after 2 seconds
        setTimeout(() => {
          setIsOpen(false)
          setSuccess(false)
        }, 2000)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : t('errors.operationFailed', 'Operation failed')
        setErrors({ submit: errorMessage })
      } finally {
        setProcessing(false)
      }
    }

    if (!isOpen) return null

    // Success state
    if (success) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {actionType === 'direct' ? t('successDirect', 'Member added successfully!') : t('successRequest', 'Member addition request submitted successfully!')}
              </h3>
              <p className="text-sm text-gray-600">
                {actionType === 'direct'
                  ? t('successDirectDesc', 'The member has been added to the project.')
                  : t('successRequestDesc', 'The request is pending approval.')
                }
              </p>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-primary/5 to-transparent">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900">{t('title', 'Add Project Member')}</h2>
                <p className="text-sm text-gray-500 mt-1">{t('subtitle', 'Add a new member to this project')}</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="ml-4 text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
                disabled={processing}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-5">
            {/* User Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('user', 'User')}
                <span className="text-red-500 ml-0.5">*</span>
              </label>

              {selectedUser && !showUserSelector ? (
                <div
                  className={`w-full px-3.5 py-2.5 border rounded-lg bg-gray-50 ${
                    errors.user ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0">
                        {selectedUser.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {selectedUser.name}
                        </div>
                        <div className="text-xs text-gray-500">@{selectedUser.account}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowUserSelector(true)}
                      className="text-sm text-primary hover:text-primary-dark font-medium"
                      disabled={processing}
                    >
                      {t('changeUser', 'Change')}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowUserSelector(true)}
                  className={`w-full px-3.5 py-2.5 border rounded-lg bg-white text-left transition-all ${
                    errors.user
                      ? 'border-red-500 hover:border-red-600'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  disabled={processing}
                >
                  <span className="text-sm text-gray-500">
                    {t('selectUser', 'Click to select user')}
                  </span>
                </button>
              )}

              {errors.user && (
                <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {errors.user}
                </p>
              )}
            </div>

              {/* User Selector Table */}
              {showUserSelector && (
                <div className="mt-3 border border-gray-300 rounded-lg overflow-hidden">
                  {/* Search Bar */}
                  <div className="p-3 bg-gray-50 border-b border-gray-200">
                    <div className="relative">
                      <input
                        type="text"
                        value={userSearchQuery}
                        onChange={(e) => {
                          setUserSearchQuery(e.target.value)
                          setUserPage(1)
                        }}
                        placeholder={t('searchUsers', 'Search users...')}
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
                      />
                      <svg
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* User Table */}
                  <div className="max-h-64 overflow-y-auto">
                    {loadingUsers ? (
                      <div className="p-8 text-center">
                        <div className="inline-block w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-sm text-gray-500 mt-2">
                          {t('loadingUsers', 'Loading users...')}
                        </p>
                      </div>
                    ) : users.length === 0 ? (
                      <div className="p-8 text-center">
                        <svg
                          className="w-12 h-12 text-gray-300 mx-auto mb-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                          />
                        </svg>
                        <p className="text-sm text-gray-500">
                          {t('noUsersFound', 'No users found')}
                        </p>
                      </div>
                    ) : (
                      <table className="w-full">
                        <tbody className="divide-y divide-gray-200">
                          {users.map((user) => (
                            <tr
                              key={user.id}
                              onClick={() => {
                                setSelectedUser(user)
                                setShowUserSelector(false)
                                setErrors((prev) => ({ ...prev, user: '' }))
                              }}
                              className="hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                              <td className="px-3 py-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0">
                                    {user.name.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-gray-900 truncate">
                                      {user.name}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">
                                      @{user.account}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-3 text-right">
                                <span className="text-xs text-gray-400">
                                  {t(`roles.${user.platform_role}`, user.platform_role)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Pagination */}
                  {!loadingUsers && users.length > 0 && (
                    <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs">
                      <button
                        type="button"
                        onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                        disabled={userPage === 1}
                        className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {t('previous', 'Previous')}
                      </button>
                      <span className="text-gray-600">
                        {t('pageInfo', 'Page {{current}} of {{total}}', {
                          current: userPage,
                          total: Math.max(1, Math.ceil(userTotalItems / userPageSize)),
                        })}
                      </span>
                      <button
                        type="button"
                        onClick={() => setUserPage((p) => p + 1)}
                        disabled={userPage >= Math.ceil(userTotalItems / userPageSize)}
                        className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {t('next', 'Next')}
                      </button>
                    </div>
                  )}

                  {/* Close Button */}
                  <div className="px-3 py-2 bg-gray-100 border-t border-gray-200 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowUserSelector(false)}
                      className="text-xs text-gray-600 hover:text-gray-800 font-medium"
                    >
                      {t('closeSelector', 'Close')}
                    </button>
                  </div>
                </div>
              )}

            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('role', 'Role')}
                <span className="text-red-500 ml-0.5">*</span>
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as any)}
                className={`w-full px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                  errors.role
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:ring-primary/30'
                }`}
                disabled={processing}
              >
                <option value="owner">{t('roles.owner', 'Owner')}</option>
                <option value="admin">{t('roles.admin', 'Admin')}</option>
                <option value="member">{t('roles.member', 'Member')}</option>
                <option value="viewer">{t('roles.viewer', 'Viewer')}</option>
                <option value="observer">{t('roles.observer', 'Observer')}</option>
              </select>
              {errors.role && (
                <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {errors.role}
                </p>
              )}
            </div>

            {/* Action Type Selection */}
            {canDirectAdd && canRequestAdd && (
              <div className="pt-2">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t('actionType', 'Action Type')}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="cursor-pointer">
                    <input
                      type="radio"
                      name="actionType"
                      value="direct"
                      checked={actionType === 'direct'}
                      onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                      disabled={processing}
                      className="sr-only"
                    />
                    <div
                      className={`h-full px-4 py-3 border-2 rounded-lg transition-all ${
                        actionType === 'direct'
                          ? 'border-primary bg-primary/10'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <span className="text-sm font-medium text-gray-900 block">
                        {t('directAdd', 'Direct Add')}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('directAddDesc', 'Immediately add the member to the project')}
                      </p>
                    </div>
                  </label>
                  <label className="cursor-pointer">
                    <input
                      type="radio"
                      name="actionType"
                      value="request"
                      checked={actionType === 'request'}
                      onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                      disabled={processing}
                      className="sr-only"
                    />
                    <div
                      className={`h-full px-4 py-3 border-2 rounded-lg transition-all ${
                        actionType === 'request'
                          ? 'border-primary bg-primary/10'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <span className="text-sm font-medium text-gray-900 block">
                        {t('submitRequest', 'Submit Request')}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('submitRequestDesc', 'Create a member addition request')}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Error Message */}
            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2.5">
                <svg
                  className="w-5 h-5 text-red-600 mt-0.5 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm text-red-700 flex-1">{errors.submit}</p>
              </div>
            )}
          </div>

            {/* Actions */}
            <div className="border-t border-gray-100 px-6 py-4 bg-gray-50/50 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-all font-medium"
                disabled={processing}
              >
                {t('cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-sm hover:shadow"
                disabled={processing}
              >
                {processing && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <span>
                  {processing
                    ? t('processing', 'Processing')
                    : actionType === 'direct'
                      ? t('addMember', 'Add Member')
                      : t('submitRequest', 'Submit Request')}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }
)

AddProjectMemberModal.displayName = 'AddProjectMemberModal'

export default AddProjectMemberModal
